'use client';

/**
 * QIBLA — boussole de la Kaaba
 * Géolocalisation + villes rapides, calcul azimut grand-cercle (21.4225 N, 39.8262 E),
 * aiguille rotative + support deviceorientation (boussole réelle sur mobile).
 */
import { useEffect, useMemo, useState } from 'react';
import { useApp } from '@/lib/store';
import { speakText } from '@/lib/speech';
import { Compass, MapPin, Loader2, Crosshair } from 'lucide-react';
import { cn } from '@/lib/utils';
import PageBanner from './PageBanner';

const KAABA = { lat: 21.4225, lng: 39.8262 };

const CITIES = [
  { name: 'Dakar', lat: 14.7167, lng: -17.4677 },
  { name: 'Touba', lat: 14.85, lng: -15.8833 },
  { name: 'Kaolack', lat: 14.1512, lng: -16.0731 },
  { name: 'Saint-Louis', lat: 16.0326, lng: -16.4818 },
  { name: 'Paris', lat: 48.8566, lng: 2.3522 },
  { name: 'New York', lat: 40.7128, lng: -74.006 },
  { name: 'Londres', lat: 51.5072, lng: -0.1276 },
  { name: 'Makkah', lat: 21.3891, lng: 39.8579 },
];

const CARDINALS = ['N', 'NE', 'E', 'SE', 'S', 'SO', 'O', 'NO'];

function qiblaAngle(lat: number, lng: number): number {
  const latR = (lat * Math.PI) / 180;
  const lngR = (lng * Math.PI) / 180;
  const kLat = (KAABA.lat * Math.PI) / 180;
  const kLng = (KAABA.lng * Math.PI) / 180;
  const y = Math.sin(kLng - lngR);
  const x = Math.cos(latR) * Math.tan(kLat) - Math.sin(latR) * Math.cos(kLng - lngR);
  return (Math.atan2(y, x) * 180 / Math.PI + 360) % 360;
}

function distanceKm(lat: number, lng: number): number {
  const R = 6371;
  const dLat = ((KAABA.lat - lat) * Math.PI) / 180;
  const dLng = ((KAABA.lng - lng) * Math.PI) / 180;
  const a = Math.sin(dLat / 2) ** 2 + Math.cos((lat * Math.PI) / 180) * Math.cos((KAABA.lat * Math.PI) / 180) * Math.sin(dLng / 2) ** 2;
  return Math.round(R * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a)));
}

export default function QiblaSection() {
  const { uiLang } = useApp();
  const t = { fr: 'fr', en: 'en', ar: 'ar' }[uiLang] as 'fr' | 'en' | 'ar';
  const [pos, setPos] = useState<{ lat: number; lng: number; city: string }>({ lat: 14.7167, lng: -17.4677, city: 'Dakar' });
  const [locating, setLocating] = useState(false);
  const [heading, setHeading] = useState<number | null>(null); // cap réel du téléphone

  const lbl = (fr: string, en: string, ar: string) => (t === 'ar' ? ar : t === 'en' ? en : fr);

  const angle = useMemo(() => qiblaAngle(pos.lat, pos.lng), [pos]);
  const dist = useMemo(() => distanceKm(pos.lat, pos.lng), [pos]);
  const cardinal = CARDINALS[Math.round(angle / 45) % 8];

  // Boussole réelle (mobile)
  useEffect(() => {
    const handler = (e: DeviceOrientationEvent) => {
      const h = (e as DeviceOrientationEvent & { webkitCompassHeading?: number }).webkitCompassHeading;
      if (typeof h === 'number') setHeading(h);
      else if (e.alpha !== null && e.alpha !== undefined) setHeading(360 - e.alpha);
    };
    window.addEventListener('deviceorientation', handler, true);
    return () => window.removeEventListener('deviceorientation', handler, true);
  }, []);

  const locate = () => {
    if (!navigator.geolocation) return;
    setLocating(true);
    navigator.geolocation.getCurrentPosition(
      (p) => { setPos({ lat: +p.coords.latitude.toFixed(4), lng: +p.coords.longitude.toFixed(4), city: lbl('Ma position', 'My position', 'موقعي') }); setLocating(false); },
      () => setLocating(false),
      { timeout: 10000 }
    );
  };

  // Rotation de l'aiguille : angle qibla − cap (si cap disponible)
  const needleRotation = heading !== null ? angle - heading : angle;

  return (
    <>
      <PageBanner
        src="/images/banners/qibla_banner.png"
        eyebrow="Qibla — القِبْلَة"
        title={lbl('Trouvez la direction de la Kaaba', 'Find the direction of the Kaaba', 'حدد اتجاه الكعبة')}
        sub={lbl('Azimut calculé par le grand cercle vers la Kaaba (21.4225° N, 39.8262° E)',
          'Great-circle azimuth calculated toward the Kaaba (21.4225° N, 39.8262° E)',
          'الاتجاه الأعظم نحو الكعبة المشرفة')}
      />
      <section id="section-qibla" className="relative mx-auto max-w-6xl px-4 py-14 sm:px-6">

      <div className="mt-10 grid items-center gap-10 lg:grid-cols-2">
        {/* Boussole */}
        <div className="flex justify-center">
          <div className="relative h-72 w-72 sm:h-80 sm:w-80">
            {/* Cadran */}
            <div className="absolute inset-0 rounded-full border-4 border-accent/40 bg-gradient-to-br from-emerald-950/80 to-black/60 shadow-[0_0_50px_rgba(212,175,55,0.15)]">
              {CARDINALS.map((c, i) => (
                <span
                  key={c}
                  className="absolute left-1/2 top-3 -translate-x-1/2 text-xs font-bold text-accent/70"
                  style={{ transform: `rotate(${i * 45}deg) translateY(0)`, transformOrigin: '50% 132px' }}
                >
                  {c}
                </span>
              ))}
              {Array.from({ length: 12 }).map((_, i) => (
                <span
                  key={i}
                  className="absolute left-1/2 top-4 h-3 w-px -translate-x-1/2 bg-accent/30"
                  style={{ transformOrigin: '50% 128px', transform: `rotate(${i * 30}deg)` }}
                />
              ))}
              {/* Kaaba au centre-haut du cadran */}
              <div className="absolute left-1/2 top-10 -translate-x-1/2 text-2xl">🕋</div>
            </div>
            {/* Aiguille */}
            <div
              className="absolute inset-0 transition-transform duration-500"
              style={{ transform: `rotate(${needleRotation}deg)` }}
            >
              <div className="absolute left-1/2 top-6 h-[46%] w-1 -translate-x-1/2 rounded-full bg-gradient-to-b from-accent to-transparent" />
              <div className="absolute left-1/2 top-[46%] h-3 w-3 -translate-x-1/2 rotate-45 bg-accent" />
            </div>
            {/* Hub */}
            <div className="absolute left-1/2 top-1/2 flex h-16 w-16 -translate-x-1/2 -translate-y-1/2 items-center justify-center rounded-full border-2 border-accent/60 bg-emerald-950">
              <Compass className="h-7 w-7 text-accent" />
            </div>
          </div>
        </div>

        {/* Contrôles */}
        <div>
          <div className="rounded-2xl border border-accent/25 bg-gradient-to-b from-emerald-950/70 to-black/30 p-6 text-center">
            <p className="text-4xl font-bold text-accent">{angle.toFixed(1)}°</p>
            <p className="mt-1 text-sm text-muted-foreground">
              {lbl(`depuis le Nord (${cardinal})`, `from North (${cardinal})`, `من الشمال (${cardinal})`)}
            </p>
            <p className="mt-3 text-sm text-foreground/80">
              🕋 {dist.toLocaleString()} km {lbl('jusqu\'à la Kaaba', 'to the Kaaba', 'إلى الكعبة')}
            </p>
            {heading !== null && (
              <p className="mt-2 text-xs text-emerald-400">
                {lbl(`Cap détecté : ${Math.round(heading)}° — l'aiguille suit votre téléphone`, `Heading detected: ${Math.round(heading)}° — needle follows your phone`, `الاتجاه: ${Math.round(heading)}°`)}
              </p>
            )}
          </div>

          <div className="mt-5 flex flex-wrap items-center gap-2">
            <button
              onClick={locate}
              className="flex items-center gap-2 rounded-full bg-accent px-5 py-2.5 text-sm font-bold text-emerald-950 hover:bg-amber-300"
            >
              {locating ? <Loader2 className="h-4 w-4 animate-spin" /> : <Crosshair className="h-4 w-4" />}
              {lbl('Détecter ma position', 'Detect my position', 'تحديد موقعي')}
            </button>
            <button
              onClick={() => speakText(`القبلة من ${pos.city} ${Math.round(angle)} درجة`, 'ar')}
              className="flex items-center gap-2 rounded-full border border-accent/40 px-5 py-2.5 text-sm text-accent hover:bg-accent/10"
            >
              🗣 {lbl('Écouter la direction', 'Hear the direction', 'استمع للاتجاه')}
            </button>
          </div>

          <p className="mt-5 text-xs font-semibold uppercase tracking-wider text-muted-foreground">
            {lbl('Villes rapides', 'Quick cities', 'مدن سريعة')}
          </p>
          <div className="mt-3 flex flex-wrap gap-2">
            {CITIES.map((c) => (
              <button
                key={c.name}
                onClick={() => setPos({ lat: c.lat, lng: c.lng, city: c.name })}
                className={cn(
                  'flex items-center gap-1.5 rounded-full border px-3.5 py-1.5 text-sm transition-colors',
                  pos.city === c.name ? 'border-accent bg-accent/20 text-accent' : 'border-accent/25 text-foreground/80 hover:bg-accent/10'
                )}
              >
                <MapPin className="h-3 w-3" /> {c.name}
              </button>
            ))}
          </div>
        </div>
      </div>
      </section>
    </>
  );
}
